import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Reusable template-adjacent bits shared between the templates list, the
 * template page, and the assessment editor's inline template creator.
 *
 * - CreateTemplateModal: small form for prompt + answer_rule + image_url.
 * - RuleSetPanel: a single rule set + its rules + dry-run + edit + delete.
 * - ParamsFromPrompt: renders the identified parameters as tags.
 * - AddRuleInline: single-row builder form on a rule set.
 */
import React, { useState } from 'react';
import { Alert, Button, Card, Empty, Form, Input, List, Modal, Popconfirm, Space, Tag, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, ExperimentOutlined, PlusOutlined } from '@ant-design/icons';
import { createRule, createTemplate, deleteRule, deleteRuleSet, dryRunTemplate, } from '../../api';
import { notifySuccess } from '../errors';
import { RuleSetModal } from './RuleSetModal';
import { TemplateImageField } from './TemplateImageField';
import { AnswerFieldFrame, PromptFieldFrame, useTemplateVariableChips, } from './VariableChips';
import { PromptDirectionToggle, usePromptDirection } from './promptDirection';
import { emptyRow, formatHebrew, parseExpression, RuleRowEditor, serializeRow, validateRow, variablesFromTemplate, } from './ruleBuilder';
const { Text } = Typography;
// -------- CreateTemplateModal --------
export function CreateTemplateModal({ open, onClose, onDone }) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const { promptRef, answerRef, promptChips, answerChips } = useTemplateVariableChips(form);
    const { dir: promptDir, setDir: setPromptDir } = usePromptDirection(form, 'prompt', open);
    const submit = async () => {
        let v;
        try {
            v = await form.validateFields();
        }
        catch {
            return;
        }
        setSubmitting(true);
        try {
            const r = await createTemplate({
                prompt: v.prompt, answer_rule: v.answer_rule, image_url: v.image_url?.trim() || null,
            });
            notifySuccess('תבנית נוצרה');
            form.resetFields();
            onDone(r.id);
        }
        catch { /* client auto-surfaces */ }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsx(Modal, { title: "\u05D9\u05E6\u05D9\u05E8\u05EA \u05EA\u05D1\u05E0\u05D9\u05EA", open: open, onCancel: onClose, onOk: submit, okText: "\u05E6\u05D5\u05E8", cancelText: "\u05D1\u05D8\u05DC", confirmLoading: submitting, destroyOnHidden: true, children: _jsxs(Form, { form: form, layout: "vertical", preserve: false, children: [_jsx(Form.Item, { name: "prompt", label: "\u05E0\u05D5\u05E1\u05D7 \u05D4\u05E9\u05D0\u05DC\u05D4", rules: [{ required: true, message: 'נוסח השאלה נדרש' }], tooltip: "\u05D8\u05E7\u05E1\u05D8 \u05D4\u05E9\u05D0\u05DC\u05D4. \u05DE\u05E9\u05EA\u05E0\u05D9\u05DD \u05E0\u05DB\u05EA\u05D1\u05D9\u05DD \u05D1\u05E1\u05D5\u05D2\u05E8\u05D9\u05D9\u05DD \u05DE\u05E1\u05D5\u05DC\u05E1\u05DC\u05D9\u05DD, \u05DC\u05DE\u05E9\u05DC: \u05DB\u05DE\u05D4 \u05D6\u05D4 {a} + {b}?", children: _jsx(PromptFieldFrame, { textAreaRef: promptRef, dir: promptDir, rows: 2, chipsEl: promptChips, toggleEl: _jsx(PromptDirectionToggle, { dir: promptDir, onChange: setPromptDir }) }) }), _jsx(Form.Item, { name: "answer_rule", label: "\u05E0\u05D5\u05E1\u05D7\u05EA \u05D4\u05EA\u05E9\u05D5\u05D1\u05D4", rules: [{ required: true, message: 'נוסחת התשובה נדרשת' }], tooltip: "\u05D4\u05D1\u05D9\u05D8\u05D5\u05D9 \u05E9\u05DE\u05D7\u05E9\u05D1 \u05D0\u05EA \u05D4\u05EA\u05E9\u05D5\u05D1\u05D4 \u05D4\u05E0\u05DB\u05D5\u05E0\u05D4, \u05DC\u05DE\u05E9\u05DC: a + b", children: _jsx(AnswerFieldFrame, { inputRef: answerRef, placeholder: "a + b", chipsEl: answerChips }) }), _jsx(Form.Item, { name: "image_url", label: "\u05EA\u05DE\u05D5\u05E0\u05D4 (\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9)", children: _jsx(TemplateImageField, {}) })] }) }));
}
// -------- ParamsFromPrompt --------
export function ParamsFromPrompt({ prompt, answerRule }) {
    const names = variablesFromTemplate(prompt, answerRule);
    return _jsx(Space, { size: 4, wrap: true, children: names.map((n) => _jsx(Tag, { children: n }, n)) });
}
// -------- RuleSetPanel + AddRuleInline --------
export function RuleSetPanel({ templateId, prompt, answerRule, ruleSet, onReload }) {
    const [dry, setDry] = useState(null);
    const [dryErr, setDryErr] = useState(null);
    const [dryBusy, setDryBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const variables = React.useMemo(() => variablesFromTemplate(prompt, answerRule), [prompt, answerRule]);
    const runDry = async () => {
        setDry(null);
        setDryErr(null);
        setDryBusy(true);
        try {
            setDry(await dryRunTemplate({ template_id: templateId, rule_set_id: ruleSet.id }));
        }
        catch (err) {
            const e = err;
            setDryErr(`${e.code}: ${e.message || 'שגיאה'}`);
        }
        finally {
            setDryBusy(false);
        }
    };
    return (_jsxs(Card, { size: "small", style: { marginTop: 12 }, title: _jsxs(Space, { children: [ruleSet.name, ruleSet.is_default && _jsx(Tag, { color: "green", children: "\u05D1\u05E8\u05D9\u05E8\u05EA \u05DE\u05D7\u05D3\u05DC" })] }), extra: _jsxs(Space, { children: [_jsx(Button, { size: "small", icon: _jsx(ExperimentOutlined, {}), onClick: runDry, loading: dryBusy, children: "\u05D1\u05D3\u05D5\u05E7" }), _jsx(Button, { size: "small", icon: _jsx(EditOutlined, {}), onClick: () => setEditing(true), children: "\u05E2\u05E8\u05D5\u05DA" }), _jsx(Popconfirm, { title: "\u05DC\u05DE\u05D7\u05D5\u05E7 \u05E1\u05D8 \u05D7\u05D5\u05E7\u05D9\u05DD?", okText: "\u05DE\u05D7\u05E7", cancelText: "\u05D1\u05D8\u05DC", onConfirm: async () => {
                        try {
                            await deleteRuleSet(ruleSet.id);
                            notifySuccess('סט נמחק');
                            onReload();
                        }
                        catch { /* client auto-surfaces */ }
                    }, children: _jsx(Button, { size: "small", danger: true, icon: _jsx(DeleteOutlined, {}) }) })] }), children: [ruleSet.rules.length === 0
                ? _jsx(Empty, { description: "\u05D0\u05D9\u05DF \u05D7\u05D5\u05E7\u05D9\u05DD \u2014 \u05D4\u05D5\u05E1\u05E3 \u05D7\u05D5\u05E7 \u05E8\u05D0\u05E9\u05D5\u05DF", image: Empty.PRESENTED_IMAGE_SIMPLE })
                : (_jsx(List, { size: "small", dataSource: ruleSet.rules, renderItem: (r) => {
                        const human = formatHebrew(parseExpression(r.rule_expression));
                        return (_jsx(List.Item, { actions: [
                                _jsx(Popconfirm, { title: "\u05DC\u05DE\u05D7\u05D5\u05E7 \u05D7\u05D5\u05E7?", okText: "\u05DE\u05D7\u05E7", cancelText: "\u05D1\u05D8\u05DC", onConfirm: async () => {
                                        try {
                                            await deleteRule(r.id);
                                            notifySuccess('חוק נמחק');
                                            onReload();
                                        }
                                        catch { /* client auto-surfaces */ }
                                    }, children: _jsx(Button, { size: "small", danger: true, icon: _jsx(DeleteOutlined, {}) }) }, "del"),
                            ], children: _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [human
                                        ? (_jsxs(_Fragment, { children: [_jsx(Text, { children: human }), _jsx(Text, { type: "secondary", style: { fontSize: 11 }, children: _jsx(Text, { code: true, style: { fontSize: 11 }, children: r.rule_expression }) })] }))
                                        : _jsx(Text, { code: true, children: r.rule_expression }), r.description && _jsx(Text, { type: "secondary", style: { fontSize: 11 }, children: r.description })] }) }));
                    } })), _jsx(AddRuleInline, { ruleSetId: ruleSet.id, variables: variables, onDone: onReload }), dryErr && _jsx(Alert, { type: "error", message: dryErr, style: { marginTop: 8 } }), dry && (_jsxs(Card, { size: "small", style: { marginTop: 8, background: '#f6ffed' }, children: [_jsxs("div", { children: [_jsx("b", { children: "\u05E9\u05D0\u05DC\u05D4 \u05DC\u05D3\u05D5\u05D2\u05DE\u05D4:" }), " ", dry.prompt] }), _jsxs("div", { children: [_jsx("b", { children: "\u05E4\u05E8\u05DE\u05D8\u05E8\u05D9\u05DD:" }), " ", _jsx(Text, { code: true, children: JSON.stringify(dry.params) })] }), _jsxs("div", { children: [_jsx("b", { children: "\u05EA\u05E9\u05D5\u05D1\u05D4:" }), " ", _jsx(Text, { code: true, children: dry.correct_answer })] })] })), _jsx(RuleSetModal, { open: editing, onClose: () => setEditing(false), onDone: () => { setEditing(false); onReload(); }, templateId: templateId, variables: variables, initial: ruleSet })] }));
}
function AddRuleInline({ ruleSetId, variables, onDone }) {
    const [row, setRow] = useState(() => emptyRow(variables));
    const [desc, setDesc] = useState('');
    const [busy, setBusy] = useState(false);
    const localError = validateRow(row);
    const disabled = !!localError || busy;
    const submit = async () => {
        if (disabled)
            return;
        const expression = serializeRow(row).trim();
        if (!expression)
            return;
        setBusy(true);
        try {
            await createRule({ rule_set_id: ruleSetId, rule_expression: expression, description: desc.trim() || null });
            notifySuccess('חוק נוסף');
            setRow(emptyRow(variables));
            setDesc('');
            onDone();
        }
        catch { /* client auto-surfaces */ }
        finally {
            setBusy(false);
        }
    };
    return (_jsxs("div", { style: { marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }, children: [_jsx(RuleRowEditor, { row: row, onChange: setRow, variables: variables }), _jsxs("div", { style: { display: 'flex', gap: 8 }, children: [_jsx(Input, { placeholder: "\u05EA\u05D9\u05D0\u05D5\u05E8 (\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9)", value: desc, onChange: (e) => setDesc(e.target.value), onPressEnter: submit, style: { flex: 1 } }), _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), loading: busy, disabled: disabled, onClick: submit, children: "\u05D4\u05D5\u05E1\u05E3" })] })] }));
}
